import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Alert, Button, Chip, Link, Stack, Typography } from '@mui/material';
import { cancelRentCarOrder } from '../../../api/endpoints/rentCar';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { BookingConfirmation } from '../../../components/booking/BookingConfirmation';
import type { PaymentResponse } from '../../../api/types/rentCar';
import type { ShoppingCart } from '../../../api/types/shoppingCart';
import { formatCurrency } from '../utils';

interface DocumentStepProps {
  shoppingCartId?: string;
  cart?: ShoppingCart | null;
  payment?: PaymentResponse;
  onStartOver: () => void;
}

export function DocumentStep({ shoppingCartId, cart, payment, onStartOver }: DocumentStepProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: () => cancelRentCarOrder({ shoppingCartId: shoppingCartId as string }),
    onSuccess: () => {
      enqueueSnackbar('Rezervasyon iptal edildi.', { variant: 'success' });
      setCancelDialogOpen(false);
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'İptal işlemi başarısız.', { variant: 'error' }),
  });

  return (
    <>
      <BookingConfirmation
        status={cart?.isFinalized ? 'success' : 'partial'}
        title="Rezervasyon Belgesi"
        references={shoppingCartId ? [{ label: 'Sepet ID', value: shoppingCartId }] : undefined}
        summary={
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {cart?.isFinalized && <Chip color="success" size="small" label="Kesinleşti" />}
              {cart?.isPassengersLocked && <Chip size="small" label="Yolcular kilitli" />}
              {cart?.isBilled && <Chip size="small" label="Faturalandı" />}
            </Stack>
            <Typography variant="body2">Kalan Tutar: {formatCurrency(cart?.remainingTotal)}</Typography>
            {payment?.continueUrl && (
              <Alert severity="info">
                3D Secure yönlendirmesi gerekiyor:{' '}
                <Link href={payment.continueUrl} target="_blank" rel="noreferrer">
                  Ödemeyi tamamlamak için tıklayın
                </Link>
              </Alert>
            )}
            <ApiErrorAlert error={cancelMutation.error} />
          </Stack>
        }
        secondaryActions={
          <Button
            variant="outlined"
            color="error"
            disabled={!shoppingCartId || cancelMutation.isPending}
            onClick={() => setCancelDialogOpen(true)}
          >
            Rezervasyonu İptal Et
          </Button>
        }
        primaryAction={{ label: 'Yeni Arama Başlat', onClick: onStartOver }}
      />

      <ConfirmDialog
        open={cancelDialogOpen}
        title="Rezervasyonu iptal et"
        description="Bu işlem geri alınamaz. Devam etmek istiyor musunuz?"
        loading={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
        onCancel={() => setCancelDialogOpen(false)}
      />
    </>
  );
}
